import { broker } from '#~/agent/broker.js';
import { Bridge } from '#~/agent/components/bridge/bridge.js';
import { LocalModeSwitch } from '#~/agent/components/local-mode/switch.js';
import * as Monitor from '#~/agent/components/monitoring/index.js';
import { useAgents } from '#~/agent/hooks.js';
import { shallow } from 'zustand/shallow';
import type { Proxy } from '#~/agent/types.js';
import { useDepot } from '#~/depot/hooks.js';
import { Loader } from '@studio/design-system/loader';
import { useObserver as useGamepadObserver } from '#~/gamepad/hooks.js';
import { useObserver as useHotKeyObserver } from '#~/hot-keys/hooks.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { roomCanControlCacheKey } from './boot-readiness.js';
import { BottomTray } from '#~/room/components/bottom-tray/index.js';
import { Content } from '#~/room/components/content/index.js';
import { LeftTray } from '#~/room/components/left-tray/index.js';
import { Production } from '#~/room/components/production/production.js';
import { RightTray } from '#~/room/components/right-tray/index.js';
import { SessionsAvatars } from '#~/room/components/sessions-avatars/sessions.js';
import { TopTray } from '#~/room/components/top-tray/index.js';
import { InviteButton } from '#~/room/components/user-invite/button.js';
import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import * as Scaffold from '../scaffold/index.js';
import { Listener } from './listener.jsx';
import type { PageProps } from './types.js';
import { Watchdog } from './watchdog/index.js';
import { useAnalytics, EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/index.js';
import { createBridgeLifecycleTracker } from '#~/analytics/domains/index.js';
import { usePremountDeviceStats } from '#~/agent/components/monitoring/summary/device-stats-hook.js';
import { DeviceLifecycleAnalyticsObserver } from './device-lifecycle-analytics-observer.js';

/** Control room `<Page>` component. */
export const Page = ({ roomId, ...props }: PageProps): ReactElement => {
  // Register the gamepad and hot-key observers.
  useGamepadObserver();
  useHotKeyObserver();

  const { track } = useAnalytics();
  const bridgeLifecycleTracker = useMemo(() => createBridgeLifecycleTracker(track), [track]);

  const agentIds = useAgents((µ) => µ.map((a) => a.id), shallow);

  const proxies = agentIds
    .map((id) => broker.acquire(id))
    .filter((µ) => µ !== undefined) as Proxy[];

  const canControl = useDepot((u) => u.roomRecords[roomId]?.permissions.canControl);

  // Default is undefined, not canControl — avoids circular dependency on fresh sessions
  const [cachedCanControl, setCachedCanControl] = usePrefState(
    roomCanControlCacheKey(roomId),
    undefined
  );

  const safeCanControl = canControl === undefined ? cachedCanControl : canControl;

  useEffect(() => {
    if (canControl === undefined) {
      return;
    }

    setCachedCanControl(canControl);
  }, [canControl, setCachedCanControl]);

  useEffect(() => {
    window.sessionStorage.setItem('iris-loaded-at', '' + +new Date() / 1000);
  }, []);

  const handleMonitoringTrayClick = () => {
    track(EVENT_NAMES.USER_OPEN_MONITOR_TRAY, EVENT_CATEGORIES.MONITORING);
  };

  usePremountDeviceStats(roomId);

  // route-aware room selection: while this room's permissions are genuinely unknown — its record
  // is not in the depot AND there's no cached value (e.g. a just-created room
  // still mid read-after-write reconcile in auth/synchronizer.tsx) — do NOT
  // commit to viewer-mode chrome. `!!safeCanControl` would collapse `undefined`
  // to `false` and render the owner's own new studio read-only until refresh.
  // A legitimate viewer is unaffected: their record IS present, so `canControl`
  // is `false` (defined), not `undefined`, and this branch is skipped.
  // Per rule: reference-rules-in-comments (ai.knowledge/global).
  if (canControl === undefined && cachedCanControl === undefined) {
    return <Loader />;
  }

  return (
    <>
      <DeviceLifecycleAnalyticsObserver roomId={roomId} />
      <Scaffold.Root
        bottom={safeCanControl ? <BottomTray /> : undefined}
        header={
          <Scaffold.Header
            center={<Production canControl={!!safeCanControl} />}
            right={
              <>
                <div className='self-stretch border-r-1 border-r-base-50' />
                <div>
                  <LocalModeSwitch />
                </div>
                <div>
                  <Bridge
                    onClick={() =>
                      bridgeLifecycleTracker.trackBridgeTrayOpened({ surface: 'room_header' })
                    }
                  />
                </div>
                {!!safeCanControl && (
                  <div onClick={handleMonitoringTrayClick}>
                    <Monitor.Root
                      className='ml-auto'
                      data-stag='interactive:control-room.header.monitoring'
                      disabled={agentIds.length === 0}
                    >
                      <Monitor.Panel agentIds={agentIds} roomId={roomId} />
                    </Monitor.Root>
                  </div>
                )}
              </>
            }
          >
            <div className='grid w-full grid-cols-[1fr_min-content] items-center' />
            <div>
              <SessionsAvatars agents={proxies} />
            </div>
            <div>
              <InviteButton roomId={roomId} />
            </div>
          </Scaffold.Header>
        }
        left={<LeftTray />}
        right={safeCanControl ? <RightTray /> : undefined}
        top={<TopTray canControl={!!safeCanControl} />}
        {...props}
      >
        <Content canControl={!!safeCanControl} key='content' />
      </Scaffold.Root>

      {!!canControl && <Listener />}
      <Watchdog />
    </>
  );
};
